/**
 * Local-first draft storage for active simulation modules.
 *
 * Persists in-progress per-module state to `localStorage` so the learner can
 * recover after accidental navigation, tab close, or browser refresh without
 * any backend draft endpoints. Drafts are keyed by schema version, user id,
 * session id, and module name, indexed for quick enumeration, bounded by TTL
 * and size, and degrade to an in-memory no-op when `localStorage` is
 * unavailable.
 *
 * @module simulationDraftStorage
 */

import { logger } from '@/lib/logger';
import type { CEFRLevel, ExamModule, ExamType, SelectionStep } from '@/types/exam-simulation';

/** Current draft envelope schema version. Bump to invalidate older drafts. */
export const DRAFT_SCHEMA_VERSION = 2;

/** Default time-to-live for a draft, in milliseconds. */
export const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

/** Maximum serialized size of a single draft envelope, in bytes. */
export const DRAFT_MAX_SIZE_BYTES = 256 * 1024;

/** Marker appended when essay content has to be truncated to fit the size cap. */
export const DRAFT_TRUNCATION_MARKER = '\n\n[…draft truncated…]';

/** Reserved bytes kept aside when measuring truncation budget. */
const TRUNCATION_SAFETY_BYTES = 2048;

/**
 * Serializable timer anchor for reading/writing module countdowns.
 *
 * Legacy: `startedAt` is wall-clock epoch-ms when the timer began;
 * remaining = `max(0, initialTimeSeconds - (now - startedAt)/1000)`.
 *
 * When `remainingAtSave` is set (new drafts), it is the seconds left at last
 * persist; hooks resume with a fresh anchor so time does not elapse while the
 * user is outside the simulation.
 */
export interface DraftTimerAnchor {
  readonly initialTimeSeconds: number;
  readonly startedAt: number;
  /** Seconds remaining at last draft write; authoritative for resume after leave. */
  readonly remainingAtSave?: number;
}

/**
 * Per-module payload. Opaque to the storage layer - each module hook owns the
 * shape it writes. Strongly typed at the call site to keep this file generic.
 */
export type DraftModulePayload = Record<string, unknown>;

/**
 * Draft envelope persisted per (user, session, module).
 */
export interface DraftEnvelope<TPayload = DraftModulePayload> {
  readonly v: number;
  readonly userId: string;
  readonly sessionId: string;
  readonly module: ExamModule;
  readonly step: SelectionStep;
  readonly examType: ExamType | null;
  readonly level: CEFRLevel | null;
  /**
   * Full list of modules the learner selected for this simulation run,
   * persisted so that the Resume flow can restore the original plan even if
   * the Zustand selection was cleared.
   */
  readonly modules: readonly ExamModule[];
  /**
   * Zero-based index of the currently-active module within `modules`.
   */
  readonly moduleIndex: number;
  readonly timer: DraftTimerAnchor | null;
  readonly updatedAt: number;
  readonly payload: TPayload;
}

/**
 * Lightweight record used by the index to enumerate drafts without having to
 * parse the full payloads.
 */
export interface DraftIndexEntry {
  readonly sessionId: string;
  readonly module: ExamModule;
  readonly updatedAt: number;
  readonly step: SelectionStep;
  readonly examType: ExamType | null;
  readonly level: CEFRLevel | null;
  readonly modules: readonly ExamModule[];
  readonly moduleIndex: number;
}

const KEY_PREFIX = 'dg:sim-draft';

let storageWarningEmitted = false;
const inMemoryFallback = new Map<string, string>();

/** Detects whether `localStorage` is actually usable (can read + write). */
function isLocalStorageUsable(): boolean {
  try {
    const probeKey = `${KEY_PREFIX}:__probe__`;
    globalThis.localStorage.setItem(probeKey, '1');
    globalThis.localStorage.removeItem(probeKey);
    return true;
  } catch {
    if (!storageWarningEmitted) {
      storageWarningEmitted = true;
      logger.warn(
        'localStorage unavailable; simulation drafts will only survive within the current tab',
      );
    }
    return false;
  }
}

function setItem(key: string, value: string): void {
  if (isLocalStorageUsable()) {
    try {
      globalThis.localStorage.setItem(key, value);
      return;
    } catch (error) {
      logger.warn('Failed to write simulation draft to localStorage', {
        key,
        error: String(error),
      });
    }
  }
  inMemoryFallback.set(key, value);
}

function getItem(key: string): string | null {
  if (isLocalStorageUsable()) {
    try {
      const stored = globalThis.localStorage.getItem(key);
      if (stored !== null) {
        return stored;
      }
    } catch {
      /* fall through to in-memory */
    }
  }
  return inMemoryFallback.get(key) ?? null;
}

function removeItem(key: string): void {
  if (isLocalStorageUsable()) {
    try {
      globalThis.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }
  inMemoryFallback.delete(key);
}

/** Per-user index key used to enumerate drafts. */
export function indexKey(userId: string): string {
  return `${KEY_PREFIX}:v${DRAFT_SCHEMA_VERSION}:${userId}:index`;
}

/** Per-(user, session, module) draft key. */
export function draftKey(userId: string, sessionId: string, module: ExamModule): string {
  return `${KEY_PREFIX}:v${DRAFT_SCHEMA_VERSION}:${userId}:${sessionId}:${module}`;
}

function readIndex(userId: string): DraftIndexEntry[] {
  const raw = getItem(indexKey(userId));
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as DraftIndexEntry[];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is DraftIndexEntry =>
        !!entry &&
        typeof entry.sessionId === 'string' &&
        typeof entry.module === 'string' &&
        typeof entry.updatedAt === 'number',
    ).map((entry) => ({
      ...entry,
      modules: Array.isArray(entry.modules) ? entry.modules : [],
      moduleIndex: typeof entry.moduleIndex === 'number' ? entry.moduleIndex : 0,
    }));
  } catch {
    return [];
  }
}

function writeIndex(userId: string, entries: DraftIndexEntry[]): void {
  setItem(indexKey(userId), JSON.stringify(entries));
}

function upsertIndex(userId: string, entry: DraftIndexEntry): void {
  const entries = readIndex(userId).filter(
    (existing) => !(existing.sessionId === entry.sessionId && existing.module === entry.module),
  );
  entries.push(entry);
  writeIndex(userId, entries);
}

function removeIndexEntry(userId: string, sessionId: string, module: ExamModule): void {
  const next = readIndex(userId).filter(
    (entry) => !(entry.sessionId === sessionId && entry.module === module),
  );
  writeIndex(userId, next);
}

/** Truncates an essay-like string to keep the envelope under the size cap. */
function truncateEssayField(
  serialized: string,
  envelope: DraftEnvelope,
  fieldName: string,
): DraftEnvelope {
  const payload = envelope.payload as Record<string, unknown>;
  const raw = payload[fieldName];
  if (typeof raw !== 'string' || raw.length === 0) {
    return envelope;
  }

  const overflow = new Blob([serialized]).size - (DRAFT_MAX_SIZE_BYTES - TRUNCATION_SAFETY_BYTES);
  if (overflow <= 0) {
    return envelope;
  }

  const keepBytes = Math.max(0, new Blob([raw]).size - overflow);
  const keepChars = Math.max(0, Math.floor(keepBytes / 2));
  const truncated = raw.slice(0, keepChars) + DRAFT_TRUNCATION_MARKER;

  return {
    ...envelope,
    payload: {
      ...payload,
      [fieldName]: truncated,
    },
  };
}

function truncateStringRecordField(
  serialized: string,
  envelope: DraftEnvelope,
  fieldName: string,
): DraftEnvelope {
  const payload = envelope.payload as Record<string, unknown>;
  const raw = payload[fieldName];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return envelope;
  }

  const entries = Object.entries(raw as Record<string, unknown>)
    .filter((entry): entry is [string, string] => typeof entry[1] === 'string')
    .sort((a, b) => new Blob([b[1]]).size - new Blob([a[1]]).size);

  if (entries.length === 0) {
    return envelope;
  }

  let overflow = new Blob([serialized]).size - (DRAFT_MAX_SIZE_BYTES - TRUNCATION_SAFETY_BYTES);
  if (overflow <= 0) {
    return envelope;
  }

  const nextRecord = { ...(raw as Record<string, unknown>) };
  for (const [key, value] of entries) {
    if (overflow <= 0) break;
    const valueBytes = new Blob([value]).size;
    if (valueBytes === 0) continue;

    const keepBytes = Math.max(0, valueBytes - overflow);
    const keepChars = Math.max(0, Math.floor(keepBytes / 2));
    const truncated = keepChars > 0 ? value.slice(0, keepChars) + DRAFT_TRUNCATION_MARKER : '';
    nextRecord[key] = truncated;
    overflow -= Math.max(0, valueBytes - new Blob([truncated]).size);
  }

  return {
    ...envelope,
    payload: {
      ...payload,
      [fieldName]: nextRecord,
    },
  };
}

function truncatePayloadField(
  serialized: string,
  envelope: DraftEnvelope,
  fieldName: string,
): DraftEnvelope {
  const payload = envelope.payload as Record<string, unknown>;
  const raw = payload[fieldName];
  if (typeof raw === 'string') {
    return truncateEssayField(serialized, envelope, fieldName);
  }
  return truncateStringRecordField(serialized, envelope, fieldName);
}

/**
 * Serializes and size-guards a draft envelope.
 * Known large fields ("essayContent", "notes", "notesByTask") are truncated rather than
 * losing the entire draft if the payload exceeds {@link DRAFT_MAX_SIZE_BYTES}.
 */
function serializeWithGuard(envelope: DraftEnvelope): string | null {
  const initial = JSON.stringify(envelope);
  const initialSize = new Blob([initial]).size;
  if (initialSize <= DRAFT_MAX_SIZE_BYTES) {
    return initial;
  }

  let guarded = envelope;
  for (const field of ['essayContent', 'notes', 'notesByTask']) {
    const serialized = JSON.stringify(guarded);
    if (new Blob([serialized]).size <= DRAFT_MAX_SIZE_BYTES) {
      return serialized;
    }
    guarded = truncatePayloadField(serialized, guarded, field);
  }

  const finalSerialized = JSON.stringify(guarded);
  const finalSize = new Blob([finalSerialized]).size;
  if (finalSize > DRAFT_MAX_SIZE_BYTES) {
    logger.warn('Simulation draft exceeds size cap after truncation; dropping payload', {
      sessionId: envelope.sessionId,
      module: envelope.module,
      finalSize,
    });
    return null;
  }

  logger.warn('Simulation draft truncated to fit size cap', {
    sessionId: envelope.sessionId,
    module: envelope.module,
    originalSize: initialSize,
    finalSize,
  });
  return finalSerialized;
}

/**
 * Persists a draft envelope.
 *
 * @returns `true` if the draft was written, `false` if it was skipped (e.g.
 * after truncation still overflowed the size cap, or if inputs are invalid).
 */
export function writeDraft(envelope: DraftEnvelope): boolean {
  if (!envelope.userId || !envelope.sessionId || !envelope.module) {
    return false;
  }
  const withVersion: DraftEnvelope = { ...envelope, v: DRAFT_SCHEMA_VERSION };
  const serialized = serializeWithGuard(withVersion);
  if (!serialized) {
    return false;
  }
  setItem(draftKey(envelope.userId, envelope.sessionId, envelope.module), serialized);
  upsertIndex(envelope.userId, {
    sessionId: envelope.sessionId,
    module: envelope.module,
    updatedAt: envelope.updatedAt,
    step: envelope.step,
    examType: envelope.examType,
    level: envelope.level,
    modules: envelope.modules,
    moduleIndex: envelope.moduleIndex,
  });
  return true;
}

/**
 * Reads a single draft for `(userId, sessionId, module)`.
 *
 * Drafts older than {@link DRAFT_TTL_MS} or with a mismatched schema version
 * are discarded and removed opportunistically.
 */
export function readDraft<TPayload = DraftModulePayload>(
  userId: string,
  sessionId: string,
  module: ExamModule,
  now: number = Date.now(),
): DraftEnvelope<TPayload> | null {
  if (!userId || !sessionId) return null;
  const raw = getItem(draftKey(userId, sessionId, module));
  if (!raw) return null;
  try {
    const envelope = JSON.parse(raw) as DraftEnvelope<TPayload>;
    if (envelope.v !== DRAFT_SCHEMA_VERSION) {
      clearDraft(userId, sessionId, module);
      return null;
    }
    if (now - envelope.updatedAt > DRAFT_TTL_MS) {
      clearDraft(userId, sessionId, module);
      return null;
    }
    return envelope;
  } catch {
    clearDraft(userId, sessionId, module);
    return null;
  }
}

/**
 * Reads the pinned question order saved in a module's draft.
 *
 * Used by the module hooks on mount (in a `useState` initializer) to pin the
 * resume fetch to the exact questions the learner saw, so the backend's
 * per-request shuffle can't swap in a different set/alternate. Returns
 * `undefined` when there is no usable draft (fresh runs), so callers can fall
 * through to the normal adaptive selection.
 */
export function readDraftQuestionOrder(
  userId: string | null | undefined,
  sessionId: string | null | undefined,
  module: ExamModule,
): string[] | undefined {
  if (!userId || !sessionId) return undefined;
  const existing = readDraft(userId, sessionId, module);
  const order = (existing?.payload as { questionOrder?: unknown } | undefined)?.questionOrder;
  return Array.isArray(order) && order.length > 0 ? (order as string[]) : undefined;
}

/**
 * Lists all non-expired drafts for a user, sorted newest-first.
 * Purges stale entries from the index and from storage as a side effect.
 */
export function listDrafts(userId: string, now: number = Date.now()): DraftIndexEntry[] {
  if (!userId) return [];
  const entries = readIndex(userId);
  const live: DraftIndexEntry[] = [];
  let changed = false;

  for (const entry of entries) {
    if (now - entry.updatedAt > DRAFT_TTL_MS) {
      removeItem(draftKey(userId, entry.sessionId, entry.module));
      changed = true;
      continue;
    }
    const raw = getItem(draftKey(userId, entry.sessionId, entry.module));
    if (!raw) {
      changed = true;
      continue;
    }
    live.push(entry);
  }

  if (changed) {
    writeIndex(userId, live);
  }

  return [...live].sort((a, b) => b.updatedAt - a.updatedAt);
}

/**
 * Returns the newest draft row suitable for recovery UI when selection metadata
 * is complete; otherwise `null` (same criteria as Exam Simulation recovery).
 */
export function getNewestRecoverableDraft(
  userId: string,
  now: number = Date.now(),
): DraftIndexEntry | null {
  const drafts = listDrafts(userId, now);
  const newest = drafts[0];
  if (!newest?.examType || !newest.level || newest.modules.length === 0) {
    return null;
  }
  return newest;
}

/** Removes a single draft and its index entry. */
export function clearDraft(userId: string, sessionId: string, module: ExamModule): void {
  if (!userId || !sessionId) return;
  removeItem(draftKey(userId, sessionId, module));
  removeIndexEntry(userId, sessionId, module);
}

/** Removes every draft belonging to a user. */
export function clearAllForUser(userId: string): void {
  if (!userId) return;
  const entries = readIndex(userId);
  for (const entry of entries) {
    removeItem(draftKey(userId, entry.sessionId, entry.module));
  }
  removeItem(indexKey(userId));
}

/**
 * Computes the current `timeRemaining` (in seconds) from a persisted anchor.
 * When `remainingAtSave` is present, returns that value (floor, clamped at 0).
 * Otherwise uses legacy wall-clock math from `startedAt` and `initialTimeSeconds`.
 */
export function computeTimeRemaining(
  anchor: DraftTimerAnchor,
  now: number = Date.now(),
): number {
  if (anchor.remainingAtSave != null) {
    return Math.max(0, Math.floor(anchor.remainingAtSave));
  }
  const elapsedSeconds = Math.max(0, (now - anchor.startedAt) / 1000);
  return Math.max(0, Math.floor(anchor.initialTimeSeconds - elapsedSeconds));
}
